import { Flex, IconButton, Image, Text } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { useStores } from "stores/rootStore";
import { CartItem, getCartItemKey } from "stores/marketPlaceStore";
import { allProducts } from "helpers/fakeData";
import QuantityPicker from "@features/marketplace/cart/QuantityPicker";
import { FaTrash } from "react-icons/fa";
import { useCallback, useMemo } from "react";

interface CartItemDetailsProps {
  item: CartItem;
  onDelete: (item: CartItem) => void;
  onQuantityChange: (item: CartItem, quantity: number) => void;
}

const CartItemDetails = ({
  item,
  onDelete,
  onQuantityChange,
}: CartItemDetailsProps) => {
  const productId = item.productId;
  const product = allProducts.find((p) => p.id === productId);

  const handleQuantityChange = useCallback(
    (quantity: number) => {
      onQuantityChange(item, quantity);
    },
    [item, onQuantityChange]
  );

  const handleDelete = useCallback(() => {
    onDelete(item);
  }, [item, onDelete]);

  if (product == null) {
    return null;
  }

  const image = useMemo(() => {
    if (item.color == null) {
      return product.defaultImage;
    }
    return product.imagesByColor?.[item.color] ?? product.defaultImage;
  }, [product, item.color]);

  return (
    <Flex
      gap={4}
      p={4}
      borderWidth={1}
      borderColor="border.light"
      borderRadius={4}
      w="100%"
      h="fit-content"
    >
      <Image h="150px" src={image} alt={product.title} fit="contain" />
      <Flex flexDirection={"column"} gap={2} w="100%">
        <Text>{product.title}</Text>
        <Text textStyle="lg" fontWeight="medium" letterSpacing="tight">
          {product.currency}
          {product.price}
        </Text>
        <Flex gap={2} color="text.secondary" flex={1}>
          <Text>{item.size}</Text>
          <Text>{item.color}</Text>
        </Flex>
        <Flex
          alignItems="center"
          gap={2}
          w="100%"
          justifyContent="space-between"
        >
          <QuantityPicker
            quantity={item.quantity}
            onChange={handleQuantityChange}
          />
          <IconButton size="2xs" variant="ghost" onClick={handleDelete}>
            <FaTrash />
          </IconButton>
        </Flex>
      </Flex>
    </Flex>
  );
};

const CartList = () => {
  const { marketPlaceStore } = useStores();
  const cartItems = marketPlaceStore.cart;

  const handleDelete = useCallback(
    (item: CartItem) => {
      marketPlaceStore.removeFromCart(item);
    },
    [marketPlaceStore]
  );

  const handleQuantityChange = useCallback(
    (item: CartItem, quantity: number) => {
      marketPlaceStore.addToCart({ ...item, quantity });
    },
    [marketPlaceStore]
  );

  if (cartItems.length === 0) {
    return (
      <Flex
        h="100%"
        w="100%"
        justifyContent="center"
        alignItems="center"
        color="text.secondary"
      >
        No items in cart
      </Flex>
    );
  }

  return (
    <Flex w="100%" h="100%" flexDirection={"column"} gap={4}>
      {cartItems.map((item) => (
        <CartItemDetails
          key={getCartItemKey(item)}
          item={item}
          onDelete={handleDelete}
          onQuantityChange={handleQuantityChange}
        />
      ))}
    </Flex>
  );
};

export default observer(CartList);
